import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface Fabric {
  id: number;
  name: string;
  type: string;
  color: string;
  pattern: string;
  price_per_meter: number;
  available_meters: number;
  description: string;
  care_instructions: string;
  image_url: string;
  is_available: boolean;
}

const FALLBACK_GRADIENTS = [
  'from-sky-300 via-blue-400 to-indigo-400',
  'from-purple-300 via-violet-400 to-purple-500',
  'from-rose-300 via-red-400 to-pink-500',
  'from-emerald-300 via-green-400 to-teal-400',
  'from-amber-300 via-yellow-400 to-orange-400',
  'from-pink-300 via-rose-400 to-fuchsia-400',
];

const TelasPage: React.FC = () => {
  const [fabrics, setFabrics] = useState<Fabric[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

  useEffect(() => {
    fetch('/api/v1/fabrics')
      .then(res => res.json())
      .then(data => {
        setFabrics(data.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleImageError = (id: number) => {
    setImageErrors(prev => ({ ...prev, [id]: true }));
  };

  return (
    <div className="relative">
      {/* Page Header */}
      <section className="relative overflow-hidden py-16 md:py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-pink-50/50" />
        <div className="absolute top-10 left-20 w-48 h-48 bg-purple-400/10 rounded-full blur-[80px]" />
        <div className="absolute bottom-10 right-20 w-64 h-64 bg-pink-400/10 rounded-full blur-[80px]" />
        
        <div className="container-custom relative text-center">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-pink-500 mb-3 animate-fade-in">Materiales</h3>
          <h1 className="text-4xl md:text-5xl font-bold font-display text-gray-900 mb-4 animate-slide-up">
            Catálogo de Telas
          </h1>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto animate-slide-up-delayed">
            Selecciona entre nuestras telas de la más alta calidad para tu proyecto
          </p>
        </div>
      </section>

      {/* Fabrics Grid */}
      <section className="py-12">
        <div className="container-custom">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-10 h-10 border-4 border-pink-300 border-t-pink-600 rounded-full animate-spin" />
              <p className="mt-4 text-gray-500">Cargando telas...</p>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7 stagger-children">
            {fabrics.map((fabric, idx) => (
              <div 
                key={fabric.id} 
                className="group bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden hover:shadow-xl hover:shadow-pink-500/5 hover:-translate-y-2 transition-all duration-500 glow-hover"
              >
                {/* Image or gradient fallback */}
                <div className="relative h-48 overflow-hidden">
                  {fabric.image_url && !imageErrors[fabric.id] ? (
                    <img
                      src={fabric.image_url}
                      alt={fabric.name}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={() => handleImageError(fabric.id)}
                    />
                  ) : (
                    <div className={`absolute inset-0 bg-gradient-to-br ${FALLBACK_GRADIENTS[idx % FALLBACK_GRADIENTS.length]}`} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-white/40 to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-lg mb-1 text-gray-900">{fabric.name}</h3>
                  <p className="text-xs text-pink-500 font-medium mb-2 capitalize">{fabric.type} · {fabric.color}</p>
                  <p className="text-gray-500 text-sm mb-3 leading-relaxed">{fabric.description}</p>
                  <div className="flex items-center justify-between">
                    <p className="text-pink-600 font-bold text-lg">${fabric.price_per_meter}/metro</p>
                    <span className="text-xs text-gray-400 bg-gray-100/80 px-3 py-1 rounded-full">Disponible</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 pb-20">
        <div className="container-custom">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-pink-50 via-purple-50 to-rose-50 border border-pink-100/50 p-10 md:p-14 text-center">
            <div className="absolute top-0 right-0 w-48 h-48 bg-pink-400/10 rounded-full blur-[80px]" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-400/10 rounded-full blur-[80px]" />
            
            <div className="relative">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 font-display">¿No encuentras lo que buscas?</h2>
              <p className="text-gray-500 mb-8 max-w-xl mx-auto leading-relaxed">
                Contamos con más de 200 tipos de telas diferentes. Contáctanos para ver nuestro catálogo completo.
              </p>
              <Link 
                to="/contacto" 
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-2xl font-semibold shadow-xl shadow-pink-500/20 hover:shadow-2xl hover:shadow-pink-500/30 hover:-translate-y-0.5 transition-all duration-300"
              >
                Ver Catálogo Completo
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TelasPage;